import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Landing } from './features/landing/LandingPage';
import { Login } from './features/auth/Login';
import { Register } from './features/auth/Register';
import { ChatPage } from './features/chat/chat';
import { auth, authReady, db } from './services/firebase';
import { getSavedSession } from './services/autoLogin';
import { doc, getDoc } from 'firebase/firestore';
import { PrivacyPolicy } from './features/landing/PrivacyPolicy';
import { CallManager } from './features/chat/Callmanager';

type AuthStatus = 'checking' | 'authenticated' | 'incomplete' | 'guest';

// Hook compartido: resuelve si hay sesión activa esperando a Firebase
// También verifica que el usuario tenga username y password configurados
const useAuthCheck = () => {
  const [status, setStatus] = useState<AuthStatus>('checking');

  useEffect(() => {
    const checkUser = async (user: any) => {
      if (!user) {
        setStatus(getSavedSession() ? 'checking' : 'guest');
        return;
      }

      // Verify user has username and password set in Firestore
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const data = userDoc.data();
        const hasUsername = !!(data?.username);
        const hasPassword = !!(data?.hasPassword);

        // Google users that haven't set username or password go back to register
        if (!hasUsername || !hasPassword) {
          setStatus('incomplete');
          return;
        }
      } catch {
        // If we can't check, allow access to avoid locking out users
      }

      setStatus('authenticated');
    };

    authReady.then(() => checkUser(auth.currentUser));

    const unsub = auth.onAuthStateChanged((user) => {
      if (user) {
        checkUser(user);
      } else if (!getSavedSession()) {
        setStatus('guest');
      }
    });
    return () => unsub();
  }, []);

  return status;
};

// Ruta raíz: si ya hay sesión → /chat directamente, sin pasar por Landing
const RootRoute = () => {
  const status = useAuthCheck();
  if (status === 'checking') return null;
  if (status === 'authenticated') return <Navigate to="/chat" replace />;
  if (status === 'incomplete') return <Navigate to="/register" replace />;
  return <Landing />;
};

// Ruta protegida: verifica sesión + perfil completo
const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const status = useAuthCheck();
  if (status === 'checking') return null;
  if (status === 'guest') return <Navigate to="/" replace />;
  if (status === 'incomplete') return <Navigate to="/register" replace />;
  return children;
};

function App() {
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    authReady.then(() => {
      setCurrentUser(auth.currentUser);
      auth.onAuthStateChanged(user => setCurrentUser(user));
    });
  }, []);

  return (
    <>
      {currentUser && <CallManager currentUser={currentUser} />}
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;